<script setup lang="ts">
import { EButton, EEmptyState, ESkeleton, useToast } from '@erpx/ui'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import QueryError from '@/components/QueryError.vue'
import { problemText } from '@/i18n/problem'
import {
  BELL_PAGE_SIZE,
  listNotifications,
  markAllRead,
  markRead,
  NOTIFICATION_KEYS,
  type NotificationDTO,
} from '@/modules/notifications/api'
import NotificationItem from '@/modules/notifications/NotificationItem.vue'

import './notifications.css'

/**
 * The bell's panel: the most recent notifications, and a way out of them.
 *
 * **It lists recent rows, read and unread alike, newest first** — the order the
 * server returns. A panel that showed only unread would empty itself the moment
 * "Mark all read" was pressed, and would hide the thing a user most often opens
 * a bell to re-find: the message they read an hour ago and now need the link
 * from. Unread is marked on each row; the badge on the bell counts it.
 *
 * **Focus is trapped and Escape closes** (§11's WCAG 2.1 AA line). The panel is
 * a `dialog` that takes focus when it opens; Tab cycles within it, Escape hands
 * focus back to the bell. Without the trap, Tab from the last row walks into the
 * page behind an overlay the user cannot see they are still inside.
 *
 * It deliberately holds no dismiss action. Deleting a notification is a
 * decision, and decisions belong on the inbox screen where the row is not about
 * to disappear under the pointer.
 */

const props = defineProps<{
  /** From the bell, so "Mark all read" knows about rows past this page. */
  unreadCount: number
}>()

const emit = defineEmits<{ close: []; navigate: [] }>()

const { t } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const root = ref<HTMLElement | null>(null)
const busy = ref(false)

const { data, isPending, error, refetch } = useQuery({
  queryKey: NOTIFICATION_KEYS.listPage({ scope: 'bell' }),
  queryFn: () => listNotifications({ unreadOnly: false, limit: BELL_PAGE_SIZE }),
})

const items = computed<NotificationDTO[]>(() => data.value?.items ?? [])

const canMarkAll = computed(() => props.unreadCount > 0 && !busy.value)

/* ------------------------------------------------------------------ writes */

async function refresh(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all })
}

async function run(action: () => Promise<void>): Promise<void> {
  if (busy.value) return
  busy.value = true
  try {
    await action()
    await refresh()
  } catch (failure) {
    toast.error(t('notifications.actionFailed'), problemText(failure))
  } finally {
    busy.value = false
  }
}

function onMarkRead(item: NotificationDTO): void {
  void run(() => markRead(item.id))
}

function onMarkAll(): void {
  void run(async () => {
    await markAllRead()
    toast.success(t('notifications.allMarkedRead'), '')
  })
}

/**
 * Following a deep link marks the row read.
 *
 * The navigation happens through `RouterLink`, so it is a real anchor the
 * keyboard and a middle-click both understand; this only records that the
 * message has been dealt with. A failure here is deliberately silent: the user
 * has already left for the document, and a toast about bookkeeping on the
 * screen they just arrived at would be noise.
 */
function onActivate(item: NotificationDTO): void {
  emit('navigate')
  void markRead(item.id)
    .then(() => refresh())
    .catch(() => undefined)
}

/* ------------------------------------------------------- focus containment */

/**
 * The trap listens on `document` in the capture phase, not on the panel root.
 *
 * A handler bound to the root only sees keys pressed while focus is already
 * inside it — which is exactly the case it cannot rely on, since the failure a
 * trap exists to prevent is focus having escaped. Capturing at the document is
 * also what makes Escape work when the focused element is a control that stops
 * propagation of its own key handling. One listener, one code path: binding
 * `@keydown` on the root as well would run every branch below twice, moving
 * focus two stops per Tab.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusable(): HTMLElement[] {
  return root.value === null ? [] : [...root.value.querySelectorAll<HTMLElement>(FOCUSABLE)]
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation()
    emit('close')
    return
  }
  if (event.key !== 'Tab') return

  const stops = focusable()
  if (stops.length === 0) {
    event.preventDefault()
    return
  }
  const first = stops[0]
  const last = stops[stops.length - 1]
  const active = document.activeElement

  if (event.shiftKey && (active === first || active === root.value)) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first?.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown, true)
  void nextTick(() => {
    const stops = focusable()
    if (stops.length > 0) stops[0]?.focus()
    else root.value?.focus()
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown, true)
})
</script>

<template>
  <div
    ref="root"
    class="notif-panel"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    :aria-label="t('notifications.panel.label')"
    data-testid="notif-panel"
  >
    <div class="notif-panel__header">
      <h2 class="notif-panel__title">{{ t('notifications.panel.title') }}</h2>

      <EButton
        variant="ghost"
        size="sm"
        :disabled="!canMarkAll"
        data-testid="notif-mark-all"
        @click="onMarkAll"
      >
        {{ t('notifications.markAllRead') }}
      </EButton>

      <EButton
        variant="ghost"
        size="sm"
        icon-only
        :aria-label="t('notifications.panel.close')"
        data-testid="notif-panel-close"
        @click="emit('close')"
      >
        <template #icon><AppIcon name="close" :size="14" /></template>
      </EButton>
    </div>

    <div class="notif-panel__body">
      <QueryError
        v-if="error"
        :error="error"
        :title="t('notifications.loadFailed')"
        @retry="refetch()"
      />

      <ESkeleton v-else-if="isPending" variant="row" :lines="4" />

      <EEmptyState
        v-else-if="items.length === 0"
        compact
        icon="inbox"
        :title="t('notifications.panel.emptyTitle')"
        :description="t('notifications.panel.emptyDescription')"
        data-testid="notif-panel-empty"
      />

      <ul v-else class="notif-list" data-testid="notif-panel-list">
        <NotificationItem
          v-for="item in items"
          :key="item.id"
          :item="item"
          compact
          @activate="onActivate"
          @mark-read="onMarkRead"
        />
      </ul>
    </div>

    <div class="notif-panel__footer">
      <RouterLink to="/notifications" data-testid="notif-view-all" @click="emit('navigate')">
        {{ t('notifications.panel.viewAll') }}
      </RouterLink>
    </div>
  </div>
</template>
